/**
 * Sanitizes a string to be used as an S3 key.
 * Replaces spaces with underscores and removes characters that are not
 * alphanumeric, underscores, hyphens, or periods.
 */
export const sanitizeS3Key = (key: string): string => {
  return key
    .replaceAll(/\s+/g, '_') // Replace spaces with underscores
    .replaceAll(/[^a-zA-Z0-9._-]/g, '_'); // Remove non-S3-safe characters
};

export type S3UploadType =
  | 'reels'
  | 'thumbnails'
  | 'videos'
  | 'additional-images'
  | 'broadcast-thumbnails'
  | 'channel-banner'
  | 'channel-profile';

/**
 * Generates a timestamped S3 key for a reel or thumbnail.
 * Example: images/{uid}/reels/test_1711204800.mp4
 * Channel: images/{uid}/channel/banner|profile/{name}_{ts}.{ext}
 */
export const generateS3Key = (
  uid: string,
  filename: string,
  type: S3UploadType,
  folder: 'reels' | 'videos' = 'reels',
): string => {
  const sanitizedFilename = sanitizeS3Key(filename);
  const timestamp = Math.floor(Date.now() / 1000);
  const extension = sanitizedFilename.split('.').pop() || '';
  const nameWithoutExtension = sanitizedFilename.substring(0, sanitizedFilename.lastIndexOf('.')) || sanitizedFilename;
  const stampedName = `${nameWithoutExtension}_${timestamp}.${extension || 'jpg'}`;

  if (type === 'thumbnails') {
    return `images/${uid}/${folder}/thumbnail/${stampedName}`;
  }

  if (type === 'broadcast-thumbnails') {
    return `images/reels/thumbnail/${stampedName}`;
  }

  if (type === 'additional-images') {
    return `images/${uid}/${folder}/additionalImages/${stampedName}`;
  }

  if (type === 'channel-banner') {
    return `images/${uid}/channel/banner/${stampedName}`;
  }

  if (type === 'channel-profile') {
    return `images/${uid}/channel/profile/${stampedName}`;
  }

  return `images/${uid}/${folder}/${stampedName}`;
};
